import React, { useEffect, useState } from "react";
import { PageHeader, Table, Tag, Select, Input, Button } from "antd";
import { UnorderedListOutlined, SearchOutlined } from "@ant-design/icons";
import { handleApiError, MyBreadcrumb } from "utils";
import { ticketListService, ticketProjectListService } from "services/tickets";
import { TicketAccessGate } from "../_useTicketAccess";
import TicketDrawer from "../_components/TicketDrawer";
import { PRIORITY_META, STATUS_META, userDisplayName, isOverdue } from "../constants";
import { useTicketAccess } from "../_useTicketAccess";
import "../_styles.css";

function AllTicketsContent() {
  const { canManage } = useTicketAccess();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ project: "", priority: "", status: "" });
  const [search, setSearch] = useState("");
  const [drawerId, setDrawerId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.project) params.project = filters.project;
    if (filters.priority) params.priority = filters.priority;
    if (filters.status) params.status = filters.status;

    ticketListService(params)
      .then((res) => {
        setTickets(res.data.items || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        handleApiError(err);
      });
  };

  useEffect(() => {
    ticketProjectListService().then((res) => setProjects(res.data.items || [])).catch(() => {});
  }, []);

  useEffect(load, [filters]);

  const filtered = tickets.filter(
    (t) =>
      !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.ticket_number.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { title: "#", dataIndex: "ticket_number", width: 110 },
    {
      title: "Title",
      dataIndex: "title",
      render: (t, r) => (
        <a onClick={() => setDrawerId(r._id)}>{t}</a>
      ),
    },
    {
      title: "Project",
      key: "project",
      render: (_, r) => (
        <Tag color={r.project?.color}>{r.project?.name}</Tag>
      ),
    },
    {
      title: "List",
      key: "list",
      render: (_, r) => r.list?.name || "-",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      render: (p) => {
        const pm = PRIORITY_META[p] || PRIORITY_META.MEDIUM;
        return (
          <span className="priority-pill" style={{ background: pm.bg, color: pm.color }}>
            {pm.label}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => <Tag color={STATUS_META[s]?.color}>{STATUS_META[s]?.label}</Tag>,
    },
    {
      title: "Assignees",
      key: "assignees",
      render: (_, r) => (r.assignees || []).map((a) => userDisplayName(a)).join(", ") || "-",
    },
    {
      title: "Due",
      dataIndex: "due_date",
      render: (d, r) =>
        d ? <span className={isOverdue(d, r.status) ? "due-overdue" : ""}>{d}</span> : "-",
    },
  ];

  return (
    <div className="ticket-system">
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Tickets", path: "/tickets/dashboard" },
          { label: "All Tickets", path: "" },
        ]}
      />
      <PageHeader
        title="All Tickets"
        subTitle="Search and filter tickets across all projects"
        avatar={{ icon: <UnorderedListOutlined /> }}
      />

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search tickets…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
        />
        <Select
          placeholder="Project"
          allowClear
          style={{ width: 200 }}
          value={filters.project || undefined}
          onChange={(v) => setFilters({ ...filters, project: v || "" })}
        >
          {projects.map((p) => (
            <Select.Option key={p._id} value={p._id}>{p.name}</Select.Option>
          ))}
        </Select>
        <Select
          placeholder="Priority"
          allowClear
          style={{ width: 140 }}
          value={filters.priority || undefined}
          onChange={(v) => setFilters({ ...filters, priority: v || "" })}
        >
          {Object.keys(PRIORITY_META).map((p) => (
            <Select.Option key={p} value={p}>{PRIORITY_META[p].label}</Select.Option>
          ))}
        </Select>
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 140 }}
          value={filters.status || undefined}
          onChange={(v) => setFilters({ ...filters, status: v || "" })}
        >
          {Object.keys(STATUS_META).map((s) => (
            <Select.Option key={s} value={s}>{STATUS_META[s].label}</Select.Option>
          ))}
        </Select>
        <Button onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>

      <Table
        className="list-view-table"
        rowKey="_id"
        loading={loading}
        dataSource={filtered}
        columns={columns}
        pagination={{ pageSize: 25 }}
        onRow={(r) => ({ onClick: () => setDrawerId(r._id) })}
      />

      <TicketDrawer
        ticketId={drawerId}
        open={!!drawerId}
        canManage={canManage}
        onClose={() => {
          setDrawerId(null);
          load();
        }}
        onUpdated={load}
      />
    </div>
  );
}

export default function TicketAllList() {
  return (
    <TicketAccessGate>
      <AllTicketsContent />
    </TicketAccessGate>
  );
}
